/**
 * Attendu 622 — Compléter des égalités à trou multiplicatives
 * (application directe de la définition du quotient).
 *
 * 5 générateurs : saisie / QCM / relier / texte-trou.
 */

import {
    randInt,
    randomChoice,
    shuffle,
    makeQcm,
    makeSaisie,
    makeRelier,
    makeTexteTrou,
} from '../../../utils';

import {
    mkFrac,
    gcd,
    renderFrac,
} from './fraction.js';

const ATTENDU = 622;

const exprElement = (katexStr) => ({
    version: 1,
    elements: [{ type: 'expression', config: { contenu: katexStr } }],
});

/* ──────────────────────────────────────────────────────────────
 * 622-T1 — Texte à trous : b × [?/?] = a, fraction empilée à deux cases
 * Ex-champ libre (2026-08-02) : « 2/3 » tapé restait une écriture à
 * plat, et la tolérance numérique ne lit pas les fractions. Ici l'élève
 * remplit une vraie fraction. Consigne « fraction irréductible » :
 * unicité garantie par le tirage copremier (cas par cas assumé).
 * Étais \rule : la barre de fraction reste visible sous les champs.
 * Pool : 328 couples × 2 sens.
 * ────────────────────────────────────────────────────────────── */
export const genEgaliteMultTrouSaisie = () => {
    let a, b;
    do {
        b = randInt(2, 20);
        a = randInt(2, 30);
    } while (gcd(a, b) !== 1);
    const fracTrous = `\\dfrac{{{${a}}}\\rule[-0.55em]{0pt}{0.55em}}{\\rule{0pt}{1.3em}{{${b}}}}`;
    // Sens du produit à pile ou face : l'attendu couvre b × ? = a ET ? × b = a
    const phrase = Math.random() < 0.5
        ? `$${b} \\times ${fracTrous} = ${a}$`
        : `$${fracTrous} \\times ${b} = ${a}$`;
    const texte = `<p>Complète avec la <b>fraction irréductible</b> pour que l'égalité soit vraie.</p>`;
    return makeTexteTrou(phrase, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter b × ? = a (fraction à deux cases)",
        mode: 'saisie',
        strict: true,
    });
};

/* ──────────────────────────────────────────────────────────────
 * 622-T2 — Saisie : b × (?/b) = a, réponse = a (entier)
 * ────────────────────────────────────────────────────────────── */
export const genEgaliteNumMultTexte = () => {
    // b resserré 2..12 (revue 2026-08-02) : l'ancien 2..80 donnait 64 % de
    // tirages illisibles sans intérêt (la réponse est a, peu importe b).
    // Copremiers : la fraction complétée a/b doit être irréductible ;
    // a ≠ b en découle (fini les « 23 × ?/23 = 23 »). Pool : 184 couples.
    let a, b;
    do {
        b = randInt(2, 12);
        a = randInt(2, 30);
    } while (gcd(a, b) !== 1);
    const texte = `<p>Complète pour que l'égalité soit vraie :</p>{{element:0}}`;
    const result = makeSaisie(String(a), texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter b × ?/b = a (numérateur manquant)",
        strict: true,
    });
    result.elements_additionnels = exprElement(`${b} \\times \\dfrac{\\;?\\;}{${b}} = ${a}`);
    return result;
};

/* ──────────────────────────────────────────────────────────────
 * 622 — SUPPRIMÉ 2026-08-02 (frontière 621/622) : « Calcule b × a/b = ? »
 * (produit_mult_texte). Trou APRÈS le égal = geste de vérification —
 * c'est def_quotient_texte de 621, en version saine (copremiers forcés).
 * Le trou de 622 porte sur un FACTEUR : b × ? = a.
 * ────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────
 * 622-T4 — QCM : b × ? = a
 * ────────────────────────────────────────────────────────────── */
export const genEgaliteMultQcm = () => {
    // Copremiers (revue 2026-08-02) : sinon mkFrac affichait l'option
    // correcte SIMPLIFIÉE (l'élève construit 4/6, l'option montre 2/3 —
    // la simplification faisait barrage à la définition, 24 % des tirages).
    // Écart absolu |a−b| : l'ancien a−b sortait négatif dans 26 % des cas.
    // Pool : 200 couples × 2 sens.
    let a, b;
    do {
        b = randInt(2, 15);
        a = randInt(2, 25);
    } while (gcd(a, b) !== 1);
    const correct = `$${renderFrac(mkFrac(a, b))}$`;
    const distractors = [
        `$${renderFrac(mkFrac(b, a))}$`,
        `$${Math.abs(a - b)}$`,
        `$${a + b}$`,
    ];
    const expr = Math.random() < 0.5
        ? `${b} \\times \\;?\\; = ${a}`
        : `\\;?\\; \\times ${b} = ${a}`;
    const texte = `<p>Complète : {{element:0}}</p>`;
    return makeQcm([correct, ...distractors], correct, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter b × ? = a (QCM)",
        elements_additionnels: exprElement(expr),
    });
};

/* SUPPRIMÉ 2026-08-02 (egalite_mult_vf) : un V/F ne peut montrer que des
 * égalités COMPLÈTES — dès qu'on bouche le trou pour afficher la ligne,
 * on retombe sur « juger b × a/b = a », le geste de vérification, déjà
 * couvert proprement par def_quotient_vf de 621 (copremiers, commutatif).
 * Le format est structurellement incompatible avec un attendu « à trous ». */

/* ──────────────────────────────────────────────────────────────
 * 622-T6 — Relier : calcul ↔ résultat
 * ────────────────────────────────────────────────────────────── */
export const genProduitMultRelier = () => {
    // 3 nombres distincts qui tournent : b×a/b=a, a×c/a=c, c×b/c=b
    // Chaque nombre apparaît à gauche (multiplicateur) ET à droite (résultat d'un autre)
    let nums;
    do {
        nums = shuffle([randInt(2, 12), randInt(2, 12), randInt(2, 12)]);
    } while (new Set(nums).size < 3);
    const [x, y, z] = nums;
    const pairs = [
        { source: `$${x} \\times ${renderFrac(mkFrac(y, x))}$`, target: `$${y}$` },
        { source: `$${y} \\times ${renderFrac(mkFrac(z, y))}$`, target: `$${z}$` },
        { source: `$${z} \\times ${renderFrac(mkFrac(x, z))}$`, target: `$${x}$` },
    ];
    const texte = `<p>Relie chaque calcul à son résultat.</p>`;
    return makeRelier(pairs, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Relier produit multiplicatif à son résultat",
    });
};

/* ──────────────────────────────────────────────────────────────
 * 622-T7 (SUPPRIMÉ 2026-07-27, bilan prof D6) — drag b × (?/b) = ?
 * Défaut structurel : a × (n/a) = n donne toujours numérateur =
 * résultat → deux étiquettes jumelles à placer sans calculer.
 * La variante saisie (622-T2) couvre le même geste sans le défaut.
 * ────────────────────────────────────────────────────────────── */

/* ──────────────────────────────────────────────────────────────
 * 622-T8 — Texte à trous saisie : b × (?/?) = a
 *   Les deux cases sont num et dén de la fraction a/b.
 * ────────────────────────────────────────────────────────────── */
export const genFractionInconnueTrouSaisie = () => {
    const b = randInt(3, 20);
    let a = randInt(3, 30);
    if (a === b) a += 1;
    // Dénominateur imposé dans l'énoncé (bilan prof D6) : sans lui la solution
    // n'est pas unique (24/19 mais aussi 48/38…) et une réponse juste serait
    // refusée en correction stricte. En prime, le trou du bas teste le
    // vocabulaire « dénominateur ».
    const phrase = `$${b} \\times \\dfrac{{{${a}}}}{{{${b}}}} = ${a}$`;
    const texte = `<p>Complète les deux cases pour que l'égalité soit vraie. <b>La fraction doit avoir pour dénominateur ${b}.</b></p>`;
    return makeTexteTrou(phrase, texte, {
        niveau: '6eme', matiere: 'maths', attendu: ATTENDU,
        label: "Compléter une fraction inconnue dans a × ? = b",
        mode: 'saisie',
        strict: true,
    });
};

/* ──────────────────────────────────────────────────────────────
 * Registre
 * ────────────────────────────────────────────────────────────── */
export const GENERATORS = {
    egalite_mult_trou_saisie: genEgaliteMultTrouSaisie,
    egalite_num_mult_texte: genEgaliteNumMultTexte,
    egalite_mult_qcm: genEgaliteMultQcm,
    produit_mult_relier: genProduitMultRelier,
    fraction_inconnue_trou_saisie: genFractionInconnueTrouSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
    qcm: 'QCM', vf: 'Vrai/Faux', drag: 'Drag', trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    // Pas de calculatrice : compléter une égalité par la définition se
    // fait de tête (même arbitrage que 620/621, revue 2026-08-02)
    payload.contenu = { ...payload.contenu, calculatrice: false };
    payload._meta.generatorKey = key;
    return payload;
};

export const CATEGORIES = [
    { id: 'egalite_mult', label: 'Égalités à trou : b × ? = a', formatsList: buildFormats('egalite_mult') },
    { id: 'egalite_num_mult', label: 'Numérateur manquant : b × (?/b) = a', formatsList: buildFormats('egalite_num_mult') },
    { id: 'produit_mult', label: 'Produit entier × fraction', formatsList: buildFormats('produit_mult') },
    { id: 'fraction_inconnue', label: 'Fraction inconnue (deux cases)', formatsList: buildFormats('fraction_inconnue') },
];

const gen622 = { GENERATORS, generate, CATEGORIES };
export default gen622;
